import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { HOTEL } from "../data/hotel";
import type { ChapterData } from "../data/content-types";
import type { InteractableDef } from "../core/types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CASO EXTRA — "Fuego lento". Cocina de hotel: freidora, productos de limpieza, suelo grasiento. */
export class HotelScene extends WalkChapter {
  protected chapter: ChapterData = HOTEL;
  protected grid = { w: 15, h: 12 };
  protected spawn = { x: 8, y: 10 };
  protected worldSigns = [
    { id: "f_extintor", x: 1.4, y: 0.3 },
    { id: "w_suelo", x: 5.5, y: 0.3 },
    { id: "m_guantes", x: 9.5, y: 0.3 },
    { id: "e_salida", x: 13.4, y: 0.3 },
  ];
  private decor: Phaser.GameObjects.GameObject[] = [];
  private slipFx: Phaser.GameObjects.Arc[] = [];

  constructor() {
    super("hotel");
  }

  protected floorKind(x: number, y: number): P.TileKind {
    const h = (x * 928371 + y * 12347) & 255;
    return h % 18 === 0 ? "dirt" : "concrete";
  }

  private oilStain(): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const g = this.add.graphics();
    g.fillStyle(0x2a2620, 0.55); g.fillEllipse(0, 2, 42, 18);
    g.fillStyle(0x4a4238, 0.4); g.fillEllipse(-5, 0, 22, 10);
    g.fillStyle(0x8fb0c8, 0.25); g.fillEllipse(4, -1, 12, 6);
    c.add(g);
    return c;
  }

  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    switch (def.id) {
      case "suelo": return [this.oilStain()];
      case "carro": return [P.wheelbarrow(this)];
      case "cuchillos": return [P.toolbox(this, 0xb0b6ba)];
      case "medidas": return [P.machine(this, 0xf0a91b)];
      default: return super.buildInteractableArt(def);
    }
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0xb8b0a2);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xcfc7b8, 0xcfc7b8, 0xaaa294, 0xaaa294, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.58 + over);
    // azulejo sanitario de cocina (banda) + campana extractora al fondo
    const band = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    band.fillStyle(0xc9d0d2, 0.5); band.fillRect(-over, H * 0.3, W + over * 2, 26);
    band.fillStyle(0x8a9296, 0.5); band.fillRect(W * 0.28, H * 0.18, W * 0.2, 16); // campana
    this.buildWalls({ left: 0xbfc4bf, right: 0xd0d4cd, height: 172, base: 0x8f9690, texture: "tile" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };

    // encimeras de cocina y office
    put(P.labbench(this), 3.4, 1.6);
    put(P.labbench(this), 7.4, 1.6);
    put(P.labsink(this), 5.4, 1.5);
    put(P.shelving(this), 12.8, 1.6);
    put(P.watercooler(this), 1.6, 1.6);
    put(P.filecabinet(this), 13.2, 6.4);
    put(P.wastebin(this), 10.4, 3.4);
    put(P.plant(this), 2.2, 9.2);
    put(P.plant(this), 13.0, 9.4);
    put(P.sacks(this), 2.6, 6.6);
    put(P.barrel(this, 0xd8a01a), 12.4, 9.0);
    put(P.cone(this), 8.4, 7.0);
    put(P.extinguisher(this), 1.4, 4.2);
    // cazo/olla junto a la freidora (contexto)
    put(P.crate(this, 0xb0b6ba), 6.7, 3.6);

    // luminarias de cocina + charco de luz
    for (const [lx, ly] of [[4, 3], [8, 3], [12, 3], [6.5, 6.5], [10.5, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2;
      pg.fillStyle(0xfff2c0, 0.14); pg.fillEllipse(p.sx, pcy, 82, 38);
      this.decor.push(pg); this.addPulse(pg, 0.1, 0.004, 0.88);
      const lamp = P.hanglamp(this, 150); lamp.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(lamp); this.addSway(lamp);
    }

    // vapor/calor de la freidora (6,3)
    this.addRisers(6, 3, { count: 3, color: 0xe8e0d0, alpha: 0.3, r: 4, rise: 34, sp: 0.006, grow: 1.7, dyBase: -18, depth: 30000 });
    // brillo resbaladizo del suelo grasiento (9,8) si no está resuelto
    this.slipFx = [];
    if (!this.state.chapter("hotel").done) {
      this.slipFx = this.addRisers(9, 8, { count: 2, color: 0xbcd0e0, alpha: 0.16, r: 3, rise: 10, sp: 0.004, grow: 1.2, dyBase: 4, depth: 30000 });
    }

    if (this.state.chapter("hotel").done) this.onResolved();
  }

  /** Efecto: al limpiar el suelo grasiento, desaparece el brillo resbaladizo. */
  protected attendedEffect(ref: string, _live: boolean): void {
    if (ref === "suelo") { this.slipFx.forEach((c) => c.destroy()); this.slipFx = []; }
  }

  protected onResolved(): void {
    const f = this.iso(6, 3);
    this.add.text(f.sx, f.sy - 44, "EXTINTOR CLASE F", { fontSize: "9px", color: "#241d14", backgroundColor: "#f0c400", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
    const s = this.iso(9, 8);
    this.add.text(s.sx, s.sy - 34, "SUELO LIMPIO", { fontSize: "9px", color: "#fff", backgroundColor: "#2f8f57", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(71);
  }
}
